import { createBlock } from 'factories/block';
import { InteractionProps, repeat } from './interaction';
import { createLayout } from 'factories/layout';
import { createFeedbackBlock } from './feedback';
import { colors } from 'styles/variables';
import { Block, cloneBlock, Blocks } from '@chameleoncreator/creator-app';
import { flatten } from 'factories/utils';
import { createContinueBtn } from './continueBtn';
import { createEditableBtn } from './editableBtn';
import Correct_default from 'assets/Correct_loop.gif'; // Correct_default.svg';
import Incorrect_default from 'assets/Incorrect_loop.gif'; // Incorrect_default.svg';
import uuid from 'uuidv4';
import { getSId } from '../utils';
import { createMediaBlock } from './media';

const { lPink, lGrey0, lGrey2, lTeal0 } = colors;

export type SortationInteractionProps = InteractionProps & {
  answerCount: number;
};

const createSortationContainerBlock = (count: number) => {
  return createBlock('SortationContainer', [
    createBlock('Text'),
    createBlock('SortationAnswers', [
      ...repeat(
        () => createBlock('SortationAnswer', [createBlock('Text')]),
        count
      ),
    ]),
    createBlock('Text'),
  ]);
};

const createSortationFeedbackBlock = type => {
  return createBlock(
    'Feedback',
    [
      createBlock('Image', [], {
        props: {
          src: type === 'Correct' ? Correct_default : Incorrect_default,
        },
      }),
      createBlock('Text'),
      createEditableBtn({ label: 'Continue' }),
    ],
    {
      props: {
        type,
      },
    }
  );
};

const createAnswerBlock = () => {
  return createBlock('Answer', [
    createBlock('Text'),
    createBlock('SortableImage', [createBlock('Image')], {
      props: { isSortableImg: true },
    }),
  ]);
};

export const createSortationBlock = (count: number) => {
  return createBlock('Sortation', [
    createBlock('SortationQuestionText', [createBlock('Text')], {
      props: {
        type: 'SortationQuestionText',
      },
    }),
    createBlock('SortationHeading0', [createBlock('Text')]),
    createBlock('SortationHeading1', [createBlock('Text')]),
    createEditableBtn({ label: 'Submit' }),
    ...repeat(() => createAnswerBlock(), count),
  ]);
};

const contentForColumn = (count: number) => (i: number) => {
  return [
    () => createMediaBlock({ child: 1 }),
    () =>
      createBlock('InteractionColumn', [
        createSortationBlock(count),
        createMediaBlock({
          child: 0,
          isAdditionalMedia: true,
        }),
      ]),
  ][i];
};

export const addSortationOption = (
  block: Block,
  position: number
): [Block, Blocks?] => {
  if (block.type === 'Sortation') {
    const sortationOptionBlock = createAnswerBlock();

    const sortationOptionBlockRef = {
      id: sortationOptionBlock.id,
      type: 'Ref',
    };
    const [newBlocks] = flatten(sortationOptionBlock);

    const nextBlock = cloneBlock(block);
    (nextBlock.children as Block[]).splice(
      position,
      0,
      sortationOptionBlockRef
    );

    return [nextBlock, newBlocks];
  }
  return [block, undefined];
};

export const removeSortationOption = (
  block: Block,
  position: number
): [Block, Blocks?] => {
  if (block.type === 'Sortation') {
    const sortationOptionBlock = createAnswerBlock();

    const [newBlocks] = flatten(sortationOptionBlock);

    const nextBlock = cloneBlock(block);
    (nextBlock.children as Block[]).splice(position, 1);

    return [nextBlock, newBlocks];
  }
  return [block, undefined];
};

export const createSortationInteractionBlock = (
  props: SortationInteractionProps
) => {
  const {
    panelBackgroundDragging = lGrey0,
    textColorDragging = '#fff',
    baseColor = lGrey2,
    numberOfAttempts = 1,
    showCorrectAnswer = 0,
    lockDown = 0,
    score = true,
    style,
  } = props as any;

  return createBlock(
    'Interaction',
    [
      createLayout(
        contentForColumn(props.answerCount),
        props.columnCount,
        props.columnsReversed
      ),
      createFeedbackBlock({}),
      createBlock('SingleColumnLayout', [
        createBlock('Column', [createContinueBtn(style)], {
          props: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
          },
          meta: {
            subType: 'ButtonBlock',
          },
        }),
      ]),
    ],
    {
      props: {
        name: props.name,
        sId: getSId('interaction'),
        style: props.style,
        baseColor,
        numberOfAttempts,
        showCorrectAnswer,
        lockDown,
        score,
      },
      meta: {
        subType: 'Sortation',
        layout: `${props.columnCount === 0 ? 'OneColumn' : 'TwoColumns'}${
          props.columnsReversed ? 'Reversed' : ''
        }`,
      },
    }
  );
};

export const create = createSortationInteractionBlock;
export const interactionConfig = {};
